import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardTitle, Textarea, useToast } from "@/core/ui";
import { useAddComment, useDeleteComment, useUpdateComment, type ReviewComment } from "./api";

function anchorLabel(anchor: Record<string, unknown> | null): string {
  if (!anchor) return "";
  return Object.entries(anchor)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(" ");
}

export function ReviewThread({ slug, comments }: { slug: string; comments: ReviewComment[] }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const add = useAddComment(slug);
  const update = useUpdateComment(slug);
  const remove = useDeleteComment(slug);
  const [body, setBody] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!body.trim()) return;
    add.mutate(
      { body: body.trim() },
      { onSuccess: () => setBody(""), onError: () => toast(t("app.error_generic"), "danger") },
    );
  };

  return (
    <Card>
      <CardTitle>{t("backlog.review.title")}</CardTitle>
      {comments.length === 0 ? (
        <p className="bt-review__empty">{t("backlog.review.empty")}</p>
      ) : (
        <ul className="bt-review__list">
          {comments.map((comment) => (
            <li key={comment.id} className="bt-review__comment">
              <div className="bt-review__meta">
                <span className="bt-review__author">{comment.author_name}</span>
                <time dateTime={comment.created_at}>
                  {new Date(comment.created_at).toLocaleString(i18n.language)}
                </time>
                {comment.anchor ? (
                  <span>{t("backlog.review.anchor", { anchor: anchorLabel(comment.anchor) })}</span>
                ) : null}
                {comment.resolved ? (
                  <Badge tone="success">{t("backlog.review.resolved")}</Badge>
                ) : null}
              </div>
              <p className="bt-review__body">{comment.body}</p>
              <div className="bt-review__actions">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => update.mutate({ id: comment.id, resolved: !comment.resolved })}
                >
                  {comment.resolved ? t("backlog.review.reopen") : t("backlog.review.resolve")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove.mutate(comment.id)}>
                  {t("backlog.review.delete")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit}>
        <label className="bt-visually-hidden" htmlFor="review-body">
          {t("backlog.review.placeholder")}
        </label>
        <Textarea
          id="review-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("backlog.review.placeholder")}
        />
        <Button
          type="submit"
          variant="secondary"
          className="bt-review__submit"
          loading={add.isPending}
        >
          {t("backlog.review.add")}
        </Button>
      </form>
    </Card>
  );
}
